import {FrameSet} from './FrameSet';
import {from, mergeMap, Subject, takeUntil, timer} from 'rxjs';
import {
    CartesianMeasureValue,
    CartesianTools,
    ICartesianMeasureValue,
    RadarNode,
    RainComputationMap,
    RainNode,
} from 'raain-model';
import {CartesianMapValue, TimeframeContainer, TimeframeContainers} from 'raain-ui';
import {RadarService} from '../radar.service';
import {ProfileService} from '../profile.service';
import {CompareManager, ICompareTarget} from './CompareManager';
import {XYType} from '../xytype';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class RefreshManager {
    public rainComputationMapVersion: string;
    public rainComputationMapDate: Date;
    public rainComputationMapDoneDate: Date;
    public rainComputationMapLaunchedBy: string;
    public rainComputationMapTimeSpentInMs: number;

    public rainComputationMapOriginalMin: number;
    public rainComputationMapOriginalMax: number;

    public sumFn: (cartesianMapValues: CartesianMapValue[]) => number;
    public sumValues: CartesianMapValue[];

    public provider: string = 'Raain';
    public timeStepInMinutes: number = 10;
    public timeStepMode: 'eq' | 'lte' = 'eq';
    public lastError: string = '';
    public cumulative: boolean = false;

    protected closeRefreshTimer: Subject<unknown>;
    protected countsPeriod: {
        percentImages?: XYType[];
        queueRunning?: number;
        progress?: number;
    };
    protected timeframesFromRadars: FrameSet[];
    protected timeframesFromRadarsExtended: FrameSet[];
    protected timelineSelectedFrameSet: FrameSet;
    protected timeframeDates: Date[];
    protected expectedTimelineCount: number = 0;
    protected presentTimelineCount: number = 0;
    protected cartesianTools: CartesianTools;
    protected removeDuplicate: boolean;
    protected dataInternal: boolean;
    protected onRefreshInProgress: (
        countPeriods: {
            percentImages?: XYType[];
            queueRunning?: number;
            progress?: number;
        },
        timeframeDates: Date[]
    ) => Promise<void>;
    protected onRefreshDone: (timeframeDates: Date[]) => Promise<void>;
    protected onFetchDone: (timeframeContainers?: TimeframeContainers) => Promise<void>;

    constructor(
        protected radarService: RadarService,
        protected profileService: ProfileService,
        protected compareManager: CompareManager
    ) {
        this._period = {begin: new Date(), end: new Date()};
        this.closeRefreshTimer = new Subject<unknown>();
        this.cartesianTools = new CartesianTools();
        this.cleanAll();
    }

    protected _rainNode: RainNode;

    public get rainNode(): RainNode {
        return this._rainNode;
    }

    public set rainNode(rainNode: RainNode) {
        this._rainNode = rainNode;
        this.cleanAll();
    }

    protected _period: {begin: Date; end: Date};

    get period(): {begin: Date; end: Date} {
        return this._period;
    }

    set period(period: {begin: Date; end: Date}) {
        this._period.begin = new Date(period.begin);
        this._period.end = new Date(period.end);
        this.refreshCounts().then((ignored) => {});
    }

    public setMethods(
        onRefreshInProgress: (
            countPeriods: {
                percentImages?: XYType[];
                queueRunning?: number;
            },
            timeframeDates: Date[]
        ) => Promise<void>,
        onRefreshDone: (timeframeDates: Date[]) => Promise<void>,
        onFetchDone: (timeframeContainers?: TimeframeContainers) => Promise<void>
    ) {
        this.onRefreshInProgress = onRefreshInProgress;
        this.onRefreshDone = onRefreshDone;
        this.onFetchDone = onFetchDone;
    }

    cleanAll() {
        this.lastError = '';
        this.countsPeriod = {percentImages: [], queueRunning: 0, progress: 0};
        this.timeframesFromRadars = [];
        this.timeframesFromRadarsExtended = [];
        this.timeframeDates = [];
        this.removeDuplicate = true;
        this.closeRefreshTimer?.next(null);
    }

    async fetch(
        dateShown: Date,
        withGaugesMeasures: boolean,
        cumulativeHours = 0
    ): Promise<FrameSet> {
        // set the target
        const currents = this.timeframesFromRadarsExtended.filter(
            (t) => t.date.getTime() === dateShown.getTime()
        );
        if (currents.length !== 1) {
            return;
        }

        // build current Frameset
        const current: FrameSet = currents[0];
        const target = {
            ...current,
            withGauge: withGaugesMeasures,
            cumulativeHours: cumulativeHours,
        };

        const timeframeContainers = await this.refreshMapTimeframeContainers(target);

        await this.onFetchDone(timeframeContainers);
        await this.refreshCompareQuality(current);

        this.timelineSelectedFrameSet = current;
    }

    getTimelineFrameSet(): FrameSet[] {
        return this.timeframesFromRadars;
    }

    getExpectedTimelineCount(): number {
        return this.expectedTimelineCount;
    }

    getPresentTimelineCount(): number {
        return this.presentTimelineCount;
    }

    getTimelineSelectedFrameSet(): FrameSet {
        return this.timelineSelectedFrameSet;
    }

    async refresh(launchQualityAfter: boolean, dataInternal: boolean) {
        this.lastError = '';

        this.checkPeriod();

        this.dataInternal = dataInternal;

        const asyncDone = (error?: string) => {
            if (error) {
                console.error('Error in refresh', error);
            }
            this.closeRefreshTimer.next(null);
            this.onRefreshDone(this.timeframeDates).then((_ignored) => {});
        };

        const asyncRefresh = async () => {
            this.onRefreshInProgress(await this.refreshCounts(), this.timeframeDates).then(
                (_ignored) => {}
            );
            const done = await this.refreshPeriodInsidePolling();
            if (done) {
                asyncDone();
            }
        };

        timer(1000, 10000) // Wait 1s, then every 10s
            .pipe(
                mergeMap(() => from(asyncRefresh())),
                takeUntil(this.closeRefreshTimer)
            )
            .subscribe({
                error: (error) => asyncDone(JSON.stringify(error)),
            });
    }

    setRemoveDuplicate(b: boolean) {
        this.removeDuplicate = b;
    }

    public async setReportPeriod(date: Date) {
        let values: CartesianMapValue[] = [];
        for (const frameSet of this.timeframesFromRadars) {
            if (frameSet.date.getTime() !== date.getTime()) {
                continue;
            }
            values = values.concat(await this.getValues(frameSet));
        }

        this.sumFn = (cartesianMapValues: CartesianMapValue[]) => {
            return cartesianMapValues.reduce((p, v) => p + v.value, 0);
        };
        this.sumValues = values;
    }

    protected checkPeriod() {
        if (this.period.begin.getTime() === this.period.end.getTime()) {
            const message = 'need a period';
            this.lastError += message;
            throw new Error(message);
        }
    }

    protected async refreshCounts() {
        if (!this.rainNode) {
            return this.countsPeriod;
        }

        const counts = await this.profileService.getCounts(this.rainNode.id, {
            range: 'day',
            periodBegin: this._period.begin,
        });

        let progress = 0;

        // Verify progress only for SimpleZone
        if (this.rainNode.getLinks(RadarNode.TYPE).length <= 1) {
            progress = await this.profileService.getRainProgress(this.rainNode.id);
        }

        this.countsPeriod = {
            ...counts,
            progress,
        };

        return this.countsPeriod;
    }

    protected getRainComputationQualityTarget(frameset: FrameSet): string {
        if (frameset.rainComputationQualities.length <= 0) {
            return undefined;
        }
        return frameset.rainComputationQualities[0].id;
    }

    protected async refreshPeriodInsidePolling(): Promise<boolean> {
        try {
            const done = await this.refreshCheckIfDone();

            // behind refresh
            await this.refreshTimeframesFromRadars(done);
            await this.refreshGlobalCompareReport(done);

            return done;
        } catch (e) {
            console.warn(e);
            this.lastError += e.message;
        }
        return false;
    }

    protected async refreshCheckIfDone(): Promise<boolean> {
        await sleep(2000);
        return this.countsPeriod.progress === 0;
    }

    protected async refreshTimeframesFromRadars(done: boolean) {
        if (!done || !this.rainNode) {
            return [];
        }

        try {
            const beginExtended = new Date(this.period.begin);
            const endExtended = new Date(this.period.end);
            if (!this.cumulative) {
                // Only extend period for granular mode (to get neighboring frames)
                // For cumulative mode, use exact period
                beginExtended.setMinutes(beginExtended.getMinutes() - 7);
                endExtended.setMinutes(endExtended.getMinutes() + 7);
            }

            const windowInMinutes = this.cumulative
                ? Math.round((this.period.end.getTime() - this.period.begin.getTime()) / 60000)
                : this.timeStepInMinutes;

            this.rainNode = await this.profileService.getRainTimeframe(
                this.rainNode.id,
                beginExtended,
                endExtended,
                this.dataInternal,
                this.provider,
                this.timeStepInMinutes,
                windowInMinutes
            );

            this.timeframesFromRadarsExtended = await this.radarService.getTimeline(this.rainNode);
            this.timeframesFromRadars = this.timeframesFromRadarsExtended
                .filter(
                    (frameSet) =>
                        this.period.begin.getTime() < frameSet.date.getTime() &&
                        frameSet.date.getTime() <= this.period.end.getTime()
                )
                .sort((a, b) => a.date.getTime() - b.date.getTime());

            this.timeframeDates = this.timeframesFromRadars.map((frameSet) => frameSet.date);

            // Compute all expected dates
            // For cumulative mode: only 1 expected date (the period end)
            // For granular mode: all dates from period.begin to period.end with timeStepInMinutes
            const allExpectedDates: Date[] = [];
            if (this.cumulative) {
                // In cumulative mode, we expect only ONE timeframe (the cumulative)
                allExpectedDates.push(new Date(this.period.end));
            } else {
                // Start from begin + step: cumulative dates represent window ends,
                // so the first expected date is begin + timeStep, not begin itself
                const beginTime = new Date(this.period.begin).getTime();
                const currentDate = new Date(beginTime + this.timeStepInMinutes * 60000);
                while (currentDate.getTime() <= this.period.end.getTime()) {
                    allExpectedDates.push(new Date(currentDate));
                    currentDate.setMinutes(currentDate.getMinutes() + this.timeStepInMinutes);
                }
            }

            this.expectedTimelineCount = allExpectedDates.length;

            // Check if all expected dates are present in timeframeDates
            const timeframeDateTimes = new Set(this.timeframeDates.map((d) => d.getTime()));
            const missingDates = allExpectedDates.filter(
                (expectedDate) => !timeframeDateTimes.has(expectedDate.getTime())
            );
            this.presentTimelineCount = allExpectedDates.length - missingDates.length;

            if (missingDates.length > 0) {
                const message =
                    `Missing ${missingDates.length} timeframe(s) out of ${allExpectedDates.length} expected: ` +
                    missingDates.map((d) => d.toISOString()).join(', ');
                console.warn(message);
                this.lastError += message;
            }

            return this.timeframesFromRadars;
        } catch (e) {
            console.error('refreshTimeframesFromRadars', e);
        }

        return [];
    }

    protected async refreshGlobalCompareReport(done: boolean) {
        if (!done) {
            return;
        }

        try {
            const targets: ICompareTarget[] = [];
            for (const frameSet of this.timeframesFromRadars) {
                const target = JSON.parse(JSON.stringify(frameSet));
                target.date = new Date(target.date);
                targets.push(target);
            }

            await this.compareManager.refreshGlobalCompareQuality(
                targets,
                this.period,
                !this.removeDuplicate,
                this.provider,
                this.timeStepInMinutes,
                this.timeStepMode
            );
        } catch (e) {
            console.error('refreshGlobalCompareReport', e);
        }
    }

    protected async refreshMapTimeframeContainers(target: {
        date: Date;
        rainComputationCumulativeId: string;
        withGauge: boolean;
        cumulativeHours: number;
    }) {
        if (!target?.rainComputationCumulativeId) {
            const message = `Pb with refreshMapTimeframeContainers insights ${target?.date.toISOString()}`;
            console.warn(message);
            this.lastError += message;
            return null;
        }

        const rainNode = this.rainNode;
        const containers: TimeframeContainer[] = [];
        const newCreatedTimeframeContainers = new TimeframeContainers(containers);
        try {
            let rainNodeCartesianMap: RainComputationMap;

            if (target.cumulativeHours && target.rainComputationCumulativeId) {
                // For N-hour cumulative, use the last frame's cumulative (end of period)
                // The cumulative is pre-computed by raain-ground with windowInMinutes=60
                const lastFrame =
                    this.timeframesFromRadarsExtended[this.timeframesFromRadarsExtended.length - 1];
                const cumulativeId =
                    lastFrame?.rainComputationCumulativeId || target.rainComputationCumulativeId;

                // Use direct cartesian-map endpoint - cumulative data fetched from S3
                rainNodeCartesianMap =
                    await this.profileService.getRainComputationCumulativeCartesianMapById(
                        rainNode.id,
                        cumulativeId
                    );
            } else {
                rainNodeCartesianMap =
                    await this.profileService.getRainComputationCumulativeCartesianMapById(
                        rainNode.id,
                        target.rainComputationCumulativeId
                    );
            }

            if (rainNodeCartesianMap) {
                newCreatedTimeframeContainers.addFromRainComputationMap(
                    rainNodeCartesianMap,
                    false
                );

                this.rainComputationMapVersion = rainNodeCartesianMap.version;
                this.rainComputationMapDate = new Date(rainNodeCartesianMap.date);
                this.rainComputationMapDoneDate = rainNodeCartesianMap.isDoneDate;
                this.rainComputationMapLaunchedBy = rainNodeCartesianMap.launchedBy;
                this.rainComputationMapTimeSpentInMs = rainNodeCartesianMap.timeSpentInMs;

                this.rainComputationMapOriginalMin = rainNodeCartesianMap.originalDBZMin;
                this.rainComputationMapOriginalMax = rainNodeCartesianMap.originalDBZMax;
            }
        } catch (e) {
            const message = `Pb with cumulatives for id: ${target.rainComputationCumulativeId} ${e.message}`;
            console.warn(message);
            this.lastError += message;
        }

        return newCreatedTimeframeContainers;
    }

    protected async refreshCompareQuality(frameSet: FrameSet) {
        await this.compareManager.refreshCompareQuality(frameSet, true);
        await this.compareManager.setGaugesInCompare();
    }

    protected async getValues(target: FrameSet): Promise<CartesianMapValue[]> {
        if (!target?.rainComputationCumulativeId) {
            const message = `Pb with target insights ${target?.date.toISOString()}`;
            console.warn(message);
            this.lastError += message;
            return [];
        }

        let values: CartesianMapValue[] = [];
        const rainComputationMap =
            await this.profileService.getRainComputationCumulativeCartesianMapById(
                this.rainNode.id,
                target.rainComputationCumulativeId
            );

        try {
            if (rainComputationMap) {
                rainComputationMap.getMapData().forEach((m) => {
                    if (m.values) {
                        (m.values as ICartesianMeasureValue[]).forEach((v) => {
                            const cartesianMeasureValue = CartesianMeasureValue.From(v);
                            values = values.concat(
                                CartesianMapValue.From(
                                    cartesianMeasureValue.getCartesianValues(),
                                    this.cartesianTools
                                )
                            );
                        });
                    }
                });
            }
        } catch (e) {
            const message = `Pb with cumulatives for id: ${target.rainComputationCumulativeId} ${e.message}`;
            console.warn(message);
            this.lastError += message;
        }

        return values;
    }
}
